"use client";

import { useCallback, useContext, useState } from "react";
import { XIcon } from "lucide-react";
import { logEvent } from "@/service/logs/log";
import { ResourceContext } from "@/contexts/resource-context";
import { PictureAsset } from "@/components/common/picture-asset";
import { cn } from "@/lib/utils";

export const HomeBannerChannels = ({
  closeBanner,
}: {
  closeBanner: () => void;
}) => {
  const { logView } = useContext(ResourceContext);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const handleVideoLoaded = () => setIsVideoLoaded(true);

  const checkReadyOnMount = useCallback((el: HTMLVideoElement | null) => {
    if (el && el.readyState >= 3) setIsVideoLoaded(true);
  }, []);

  const handleStoreClick = (store: "app_store" | "google_play") => {
    return () =>
      logEvent("home_banner_button_click", logView, {
        banner_type: "channels",
        banner_button_type: store,
      });
  };

  const storeBadges = (
    <div className="flex shrink-0 items-center gap-2">
      <a
        href="https://play.google.com/store/apps/details?id=md.remedium.app"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Pobierz w Google Play"
        onClick={handleStoreClick("google_play")}
        className="inline-flex shrink-0 transition hover:opacity-80"
      >
        <img
          src="https://cdn.remedium.md/assets/other/konferencje-wiosna-2026/badge-google-play.png"
          alt="Pobierz w Google Play"
          className="block h-[33px] w-auto max-w-none shrink-0"
        />
      </a>
      <a
        href="https://apps.apple.com/pl/app/remedium/id1666340112?l=pl"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Pobierz w App Store"
        onClick={handleStoreClick("app_store")}
        className="inline-flex shrink-0 transition hover:opacity-80"
      >
        <img
          src="https://cdn.remedium.md/assets/other/konferencje-wiosna-2026/badge-app-store.png"
          alt="Pobierz w App Store"
          className="block h-[33px] w-auto max-w-none shrink-0"
        />
      </a>
    </div>
  );

  return (
    <div className="relative my-10 w-full overflow-hidden rounded-3xl bg-[#5024ff] md:h-[200px]">
      <PictureAsset
        source="https://cdn.remedium.md/image/common/2026/05/19/12/channels-banner-mobile-bg-png-6a0c389ec0fc3"
        ext="png"
        alt=""
        className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center md:hidden"
      />
      <PictureAsset
        source="https://cdn.remedium.md/image/common/2026/05/19/11/channels-banner-bg-png-6a0c33e688de4"
        ext="png"
        alt=""
        className="pointer-events-none absolute inset-0 hidden h-full w-full object-cover object-center md:block"
      />

      <div className="absolute top-2 right-2 z-30 cursor-pointer p-2">
        <XIcon
          size={20}
          className="text-white/70"
          onClick={() => {
            closeBanner();
          }}
        />
      </div>

      <div className="relative z-10 flex flex-col items-center gap-5 px-6 py-8 text-center md:hidden">
        <p className="text-xl leading-tight text-white">
          <span className="font-bold">Kanały </span>
          <span>w remedium app!</span>
        </p>
        <PictureAsset
          source="https://cdn.remedium.md/image/common/2026/05/19/12/channels-banner-mobile-accessory-png-6a0c386723275"
          ext="png"
          alt="Komunikator tylko dla zweryfikowanych medyków"
          className="h-auto w-full max-w-[330px] object-contain"
        />
        <div className="flex items-center gap-3">
          <p className="text-[10px] font-semibold text-white">
            Sprawdź co przygotowaliśmy!
          </p>
          {storeBadges}
        </div>
      </div>

      <div className="relative z-10 hidden h-[200px] flex-row items-center justify-between gap-4 px-6 text-left md:flex lg:gap-6 lg:px-10 xl:px-12">
        <div className="min-w-0 flex-1">
          <p className="text-2xl leading-tight tracking-[-0.21px] text-white lg:text-3xl xl:text-4xl 2xl:text-[42px]">
            <span className="font-bold">Kanały </span>
            <span>w remedium app!</span>
          </p>
          <div className="mt-3 flex flex-row items-center gap-3 lg:mt-4 lg:gap-5">
            <p className="text-xs font-semibold tracking-[-0.48px] text-white lg:text-sm xl:text-base">
              Sprawdź co przygotowaliśmy!
            </p>
            {storeBadges}
          </div>
        </div>

        <div className="pointer-events-none h-[200px] w-[320px] shrink-0 lg:w-[420px] xl:w-[500px] 2xl:w-[590px]">
          <video
            ref={checkReadyOnMount}
            autoPlay
            loop
            muted
            playsInline
            controls={false}
            onCanPlay={handleVideoLoaded}
            className={cn(
              "block h-full w-full object-contain opacity-0 transition-opacity duration-700",
              isVideoLoaded && "opacity-100",
            )}
          >
            <source
              src="https://cdn.remedium.md/assets/home/channels-banner/590x200.webm"
              type="video/webm"
            />
          </video>
        </div>
      </div>
    </div>
  );
};
